import Button from "@/components/Common/Button";
import CheckBox from "@/components/Common/CheckBox";
import InputField from "@/components/Common/forms/InputField";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import useCompanyProfileSave from "@/hooks/useCompanyProfileSave";
import {useSettingsDirty} from "@/hooks/useSettingsNavigation";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {Can} from "@/permissions";
import {useSettingsCompanyStore} from "@/store/company";
import {FULL_DATE_FORMAT} from "@/store/constants";
import type {TSettingsScope} from "@/store/types";
import dayjs from "dayjs";
import {ExternalLink, Info} from "lucide-react";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {NumericFormat} from "react-number-format";
import {useStateWithDeps} from "use-state-with-deps";

const AdminSettingsPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["common", "settings", "agency_profile"]);
  const isAdmin = useIsAdmin();
  const company = useSettingsCompanyStore(state => state.company);
  const errors = useSettingsCompanyStore(state => state.errors);
  const isSubbrand = useSettingsCompanyStore(state => state.company.company_access_level === "SUB_BRAND");
  const setValue = useSettingsCompanyStore(state => state.setValue);

  const [agencyBexioContactId, setAgencyBexioContactId] = useStateWithDeps<number | null>(
    company.agency?.bexio_contact_id ?? null,
    [company.id],
  );
  const [subbrandBexioContactId, setSubbrandBexioContactId] = useStateWithDeps<number | null>(
    company.bexio_contact_id ?? null,
    [company.id],
  );

  const {isSaving, save} = useCompanyProfileSave({
    agencyBexioContactId,
    companyId: scope.id,
    subbrandBexioContactId,
  });
  useSettingsDirty(
    useSettingsCompanyStore(state => state.hasUnsavedChanges()) ||
      agencyBexioContactId !== (company.agency?.bexio_contact_id ?? null) ||
      subbrandBexioContactId !== (company.bexio_contact_id ?? null),
  );

  if (!isAdmin) return null;

  if (company.id !== scope.id) {
    return (
      <div className="flex flex-col gap-8">
        <SkeletonLoader className="h-4.5 max-w-24" />
        <div className="flex flex-col gap-4">
          {Array.from({length: 5}).map((_, index) => (
            <div className="flex flex-col gap-2" key={index}>
              <SkeletonLoader className="h-5 max-w-32" />
              <SkeletonLoader className="h-11 rounded-lg" />
            </div>
          ))}
          <div className="flex flex-col gap-2">
            <div className="flex w-full flex-col">
              <SkeletonLoader className="h-5 max-w-40" />
              <SkeletonLoader className="h-11 rounded-md" />
            </div>
            <SkeletonLoader className="h-5 max-w-36" />
          </div>
          {Array.from({length: 3}).map((_, index) => (
            <div className="flex items-center gap-2" key={index}>
              <SkeletonLoader className="size-5 shrink-0 rounded-sm" />
              <SkeletonLoader className="h-5 max-w-40" />
            </div>
          ))}
        </div>
        <SkeletonLoader className="h-10 rounded-lg sm:max-w-32" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:settings")}</span>
      <Can I="update" a="CompanyDetails" passThrough>
        {allowed => (
          <div className="flex flex-col gap-4">
            <InputField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
              isDisabled
              label={t("agency_profile:agency_id")}
              labelClassName="text-neutral-black text-sm font-medium"
              placeholder={t("agency_profile:agency_id")}
              value={company.agency?.id?.toString() ?? ""}
            />
            <InputField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
              isDisabled
              label={t("companyId")}
              labelClassName="text-neutral-black text-sm font-medium"
              placeholder={t("companyId")}
              value={company.id?.toString() ?? ""}
            />
            {company.user?.id && (
              <InputField
                className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
                isDisabled
                label={t("companyOwnerId")}
                labelClassName="text-neutral-black text-sm font-medium"
                placeholder={t("companyOwnerId")}
                value={company.user.id?.toString() ?? ""}
              />
            )}
            <InputField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
              isDisabled
              label={t("companyRegistrationDate")}
              labelClassName="text-neutral-black text-sm font-medium"
              placeholder={t("companyRegistrationDate")}
              value={company.registration_date ? dayjs(company.registration_date).format(FULL_DATE_FORMAT) : ""}
            />
            <div className="flex flex-col gap-2">
              <div className="flex w-full flex-col">
                <label className="text-neutral-black text-sm font-medium">{t("bexio_contact_id_agency")}</label>
                <NumericFormat
                  allowNegative={false}
                  decimalScale={0}
                  className="border-neutral-grey-light-dark placeholder:text-neutral-grey-light focus:border-primary-medium-solid disabled:bg-neutral-grey-lightest disabled:text-neutral-black flex h-11 w-full rounded-md border p-4 text-sm outline-hidden transition-all duration-150 placeholder:text-sm"
                  placeholder={t("bexio_contact_id_agency")}
                  onValueChange={value => setAgencyBexioContactId(value.floatValue ?? null)}
                  value={agencyBexioContactId ?? ""}
                  disabled={!allowed || isSubbrand}
                />
              </div>
              {company.agency?.bexio_link && (
                <a
                  href={company.agency.bexio_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-dark-solid flex items-center gap-1 text-sm font-medium hover:underline">
                  <ExternalLink className="size-4" />
                  {t("bexio_link")}
                </a>
              )}
            </div>
            {isSubbrand && (
              <div className="flex flex-col gap-2">
                <div className="flex w-full flex-col">
                  <label className="text-neutral-black text-sm font-medium">{t("bexio_contact_id_subbrand")}</label>
                  <NumericFormat
                    allowNegative={false}
                    decimalScale={0}
                    className="border-neutral-grey-light-dark placeholder:text-neutral-grey-light focus:border-primary-medium-solid disabled:bg-neutral-grey-lightest disabled:text-neutral-black flex h-11 w-full rounded-md border p-4 text-sm outline-hidden transition-all duration-150 placeholder:text-sm"
                    placeholder={t("bexio_contact_id_subbrand")}
                    onValueChange={value => setSubbrandBexioContactId(value.floatValue ?? null)}
                    value={subbrandBexioContactId ?? ""}
                    disabled={!allowed}
                  />
                  <p className="text-neutral-grey-dark mt-1 mb-0 text-xs">{t("bexio_contact_id_subbrand_help")}</p>
                </div>
                {company.bexio_link && (
                  <a
                    href={company.bexio_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-dark-solid flex items-center gap-1 text-sm font-medium hover:underline">
                    <ExternalLink className="size-4" />
                    {t("bexio_link_subbrand")}
                  </a>
                )}
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <InputField
                className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
                error={errors.premium_ranking}
                isDisabled={!allowed}
                label={t("premiumRanking")}
                labelClassName="text-neutral-black text-sm font-medium"
                onChange={value => {
                  if (!/^\d*$/.test(value)) return;
                  setValue("premium_ranking", value === "" ? 0 : Number(value));
                }}
                placeholder={t("premiumRanking")}
                value={company.premium_ranking?.toString() ?? ""}
              />
              {company.agency?.disable_generative_ai && !company.premium_ranking && (
                <div className="bg-warning-light-yellow text-warning-dark-yellow flex items-start gap-2 rounded-lg p-2.5 text-xs">
                  <Info className="mt-px size-4 shrink-0" />
                  <p className="m-0">{t("premiumRankingAiDisabledNote")}</p>
                </div>
              )}
            </div>
            <CheckBox
              disabled={!allowed}
              isChecked={company.event_creator ?? false}
              label={t("eventCreator")}
              labelClassName="text-neutral-black text-sm font-medium"
              onChange={newValue => setValue("event_creator", newValue)}
            />
            <CheckBox
              disabled={!allowed}
              isChecked={company.skip_character_limits ?? false}
              label={t("skipCharacterLimits")}
              labelClassName="text-neutral-black text-sm font-medium"
              onChange={newValue => setValue("skip_character_limits", newValue)}
            />
            <CheckBox
              disabled={!allowed}
              isChecked={company.disable_chat_close ?? false}
              label={t("disableChatClose")}
              labelClassName="text-neutral-black text-sm font-medium"
              onChange={newValue => setValue("disable_chat_close", newValue)}
            />
          </div>
        )}
      </Can>
      <Button
        buttonStyle="Gradient"
        className="sticky bottom-0 h-10 w-full px-5 sm:static sm:w-fit"
        isDisabled={isSaving}
        isLoading={isSaving}
        onClick={save}>
        {t("common:saveChanges")}
      </Button>
    </div>
  );
};

export default AdminSettingsPane;
